import React, { forwardRef, useImperativeHandle, useRef, useCallback } from 'react';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import classNames from 'classnames';
import arrayMutators from 'final-form-arrays';
import debounce from 'lodash/debounce';

import { Form } from '../../../components';

import css from './FilterForm.module.css';

/**
 * FilterForm component
 * Note:
 * - If liveEdit is true, you need to provide onChange function.
 * - If liveEdit is false, you need to provide onSubmit, onCancel, and onClear functions.
 * @component
 * @param {Object} props
 * @param {boolean} [props.liveEdit] - Whether to live edit
 * @param {Function} [props.onChange] - The function to change
 * @param {Function} [props.onSubmit] - The function to submit
 * @param {Function} [props.onCancel] - The function to cancel
 * @param {Function} [props.onClear] - The function to clear
 * @param {boolean} [props.hideClearButton] - Hide the clear button in non-live mode (e.g. tree filter uses inline clear)
 * @param {Object} [props.style] - The style
 * @param {React.Node} props.children - The children
 * @returns {JSX.Element}
 */
const FilterForm = forwardRef((props, ref) => {
  const intl = useIntl();
  const {
    liveEdit = false,
    onChange,
    onSubmit,
    onCancel,
    onClear,
    initialValues,
    hideClearButton = false,
    ...rest
  } = props;

  if (liveEdit && !onChange) {
    throw new Error('FilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'FilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  // Track last submitted values to prevent duplicate onChange calls
  const lastValuesRef = useRef(null);

  // Debounce onChange for live edit to prevent excessive API calls
  const debouncedOnChange = useRef(
    debounce((values) => {
      onChange(values);
      lastValuesRef.current = values;
    }, 300)
  ).current;

  const handleChange = useCallback(
    formState => {
      if (formState.dirty) {
        // Check if values have actually changed from the last submitted values
        const valuesChanged = JSON.stringify(formState.values) !== JSON.stringify(lastValuesRef.current);
        
        // Only trigger if values have actually changed
        if (valuesChanged) {
          debouncedOnChange(formState.values);
        }
      }
    },
    [debouncedOnChange]
  );

  const formCallbacks = liveEdit ? { onSubmit: () => null } : { onSubmit, onCancel, onClear };
  
  return (
    <FinalForm
      {...rest}
      {...formCallbacks}
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          id,
          form,
          handleSubmit,
          onClear,
          onCancel,
          style,
          paddingClasses,
          children,
        } = formRenderProps;

        // Expose the form's reset method via ref
        useImperativeHandle(ref, () => ({
          reset: () => form.reset()
        }));

        const handleCancel = () => {
          // reset the final form to initialValues
          form.reset();
          onCancel();
        };

        const clear = intl.formatMessage({ id: 'FilterForm.clear' });
        const cancel = intl.formatMessage({ id: 'FilterForm.cancel' });
        const submit = intl.formatMessage({ id: 'FilterForm.submit' });

        const classes = classNames(css.root);

        return (
          <Form
            id={id}
            className={classes}
            onSubmit={handleSubmit}
            tabIndex="0"
            style={{ ...style }}
          >
            <div className={classNames(paddingClasses || css.contentWrapper)}>{children}</div>

            {liveEdit ? (
              <FormSpy onChange={handleChange} subscription={{ values: true, dirty: true }} />
            ) : (
              <div className={css.buttonsWrapper}>
                {!hideClearButton ? (
                  <button className={css.clearButton} type="button" onClick={onClear}>
                    {clear}
                  </button>
                ) : null}
                <button className={css.cancelButton} type="button" onClick={handleCancel}>
                  {cancel}
                </button>
                <button className={css.submitButton} type="submit">
                  {submit}
                </button>
              </div>
            )}
          </Form>
        );
      }}
    />
  );
});

export default FilterForm;
